import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format } from "date-fns";
import { Box, Chip, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography, IconButton, Tooltip } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import { selectJobPromotionState } from "../../state/jobPromotion/jobPromotionSelector";
import { getJobPromotions } from "../../state/jobPromotion/jobPromotionSlice";
import TableLoaderSkeleton from "../../global/Loader/TableLoaderSkeleton";
import GlobalEmptyPage from "../../common/GlobalEmptyPage/GlobalEmptyPage";
import GlobalTablePagination from "../../global/Pagination/GlobalTablePagination";
import JobPromotionStatusModal from "./JobPromotionStatusModal";

const headCells = [
  { id: "Job", label: "Job & Company" },
  { id: "Duration", label: "Duration" },
  { id: "Cost", label: "Cost" },
  { id: "Requested By", label: "Requested By" },
  { id: "Requested Date", label: "Requested Date" },
  { id: "Status", label: "Status" },
  { id: "Actions", label: "Actions" },
];

const STATUS_COLOR = {
  1: "warning", // Pending
  2: "success", // Approved
  3: "error", // Rejected
};

const STATUS_TEXT = {
  1: "Pending",
  2: "Approved",
  3: "Rejected",
};

const JobPromotions = () => {
  const dispatch = useDispatch();
  const { data: promotions, isLoading, page, perPage, count, filters } = useSelector(selectJobPromotionState);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    dispatch(getJobPromotions({ page, perPage, ...filters }));
  }, [dispatch, page, perPage, filters]);

  const onChangePage = (newPage) => {
    dispatch(getJobPromotions({ page: newPage, perPage, ...filters }));
  };

  const onChangePerPage = (newPerPage) => {
    dispatch(getJobPromotions({ page: 1, perPage: newPerPage, ...filters }));
  };

  const handleEditClick = (promotion) => {
    setSelectedPromotion(promotion);
    setIsModalOpen(true);
  };

  let content = null;
  if (isLoading) {
    content = <TableLoaderSkeleton columns={7} numberOfRows={10} />;
  } else if (promotions?.length === 0) {
    content = <GlobalEmptyPage title={"No Job Promotion requests found!"} />;
  } else if (promotions?.length > 0) {
    content = (
      <>
        <TableContainer>
          <Table sx={{ minWidth: 750 }}>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell key={headCell.id}>{headCell.label}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {promotions.map((promotion) => {
                const { status, weeks, totalCost, createdAt, jobPost, companyInfo, requestedByInfo } = promotion;
                const requestedDate = format(new Date(createdAt), "dd MMM, yyyy");

                return (
                  <TableRow key={promotion.id} hover>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2" fontWeight={700}>
                          {jobPost?.title || "Deleted Job"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {companyInfo?.name || "Unknown Company"}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {weeks} {weeks > 1 ? "Weeks" : "Week"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {totalCost} Credits
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{requestedByInfo ? `${requestedByInfo.firstName} ${requestedByInfo.lastName}` : "Unknown"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {requestedByInfo?.email}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{requestedDate}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={STATUS_TEXT[status]} color={STATUS_COLOR[status]} sx={{ fontWeight: 600, textTransform: "uppercase", fontSize: 10 }} />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Update Status">
                        <IconButton size="small" onClick={() => handleEditClick(promotion)} color="primary">
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <GlobalTablePagination count={count} page={page} rowsPerPage={perPage} onChangePerPage={onChangePerPage} onChangePage={onChangePage} />
      </>
    );
  }

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" fontWeight={700} gutterBottom>
          Job Promotion Requests
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Review and manage job promotion requests from companies.
        </Typography>
      </Paper>

      <Paper elevation={0}>{content}</Paper>

      {selectedPromotion && <JobPromotionStatusModal open={isModalOpen} onClose={() => setIsModalOpen(false)} promotion={selectedPromotion} />}
    </Box>
  );
};

export default JobPromotions;
